// src/components/common/DragDropFile.tsx

import { useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

type DragDropFileProps = {
  /** Called when user confirms the selected file */
  onConfirmFile: (file: File) => void;
};

/**
 * DragDropFile
 *
 * A lightweight drag & drop uploader for a single file.
 * - Keeps UI minimal and MUI-only
 * - Does NOT auto-upload on selection (user must confirm)
 * - Supports both drag-drop and "Choose File"
 */
const DragDropFile = ({ onConfirmFile }: DragDropFileProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  /** Treat enter/over as active, leave as inactive. */
  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragleave') {
      setDragActive(false);
      return;
    }

    setDragActive(true);
  };

  /** Accept the first file only (single-file upload). */
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) return;

    setFile(dropped);

    // Clear drag data to avoid potential browser quirks
    e.dataTransfer.clearData();
  };

  /** Handle file selection via hidden input. */
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    setFile(selected);

    // Reset input value so selecting the same file again still triggers onChange
    e.target.value = '';
  };

  const handleOpenFilePicker = () => {
    inputRef.current?.click();
  };

  const handleClear = () => {
    setFile(null);
  };

  const handleConfirm = () => {
    if (!file) return;
    onConfirmFile(file);
  };

  return (
    <Box
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      sx={(theme) => ({
        border: '2px dashed',
        borderColor: dragActive ? 'primary.main' : theme.palette.divider,
        borderRadius: 2,
        p: 4,
        textAlign: 'center',
        bgcolor: dragActive ? 'action.hover' : 'background.default',
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        userSelect: 'none',
      })}
    >
      {/* Hidden native file input (single file) */}
      <input
        ref={inputRef}
        type="file"
        hidden
        onChange={handleChange}
        // Keep it generic; backend will validate template anyway.
        // accept=".xlsx,.xls"
      />

      {!file ? (
        <Stack spacing={1.5} alignItems="center">
          <Typography variant="body1">Drag & drop your Excel file here</Typography>
          <Typography variant="body2" color="text.secondary">
            or
          </Typography>
          <Button variant="outlined" onClick={handleOpenFilePicker}>
            Choose File
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1.5} alignItems="center">
          <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
            {file.name}
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="inherit" onClick={handleClear}>
              Change
            </Button>
            <Button variant="contained" onClick={handleConfirm}>
              Confirm Upload
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Upload will start after confirmation.
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default DragDropFile;
